// Pre-upload scan for a multi-video footage batch. The peer of
// photoUpload/exifScan.js, minus EXIF: there is no browser-readable capture
// metadata inside an MP4 (and certainly not inside Brinno's MJPEG-in-MP4), so
// the only client-side signals are the file's name, size and lastModified.
//
// Deliberately does NOT try to read duration client-side. A <video> element
// refuses to load metadata for codecs it can't decode, which is precisely the
// Brinno case this feature exists for — half a batch would show a duration and
// half would not. The SERVER probe fills durationSec in for every file, so the
// preflight shows "—" for all of them and stays honest.

import { buildVideoKey } from './videoKeys';
import { defaultVideoOrder, orderingBasis, withOrder } from './ordering';

// Extensions long-shoot cameras actually write. `file.type` is authoritative
// when the browser supplies one, but it is routinely empty for .AVI/.MOV picked
// from an SD card, so the extension is the fallback rather than the reverse.
const VIDEO_EXTENSION_REGEX = /\.(mp4|m4v|mov|avi|mkv|mts|m2ts|webm)$/i;

export function isVideoFile(file) {
  if (!file) return false;
  if (typeof file.type === 'string' && file.type.startsWith('video/')) return true;
  const name = typeof file.name === 'string' ? file.name : '';
  return VIDEO_EXTENSION_REGEX.test(name);
}

// Scan a File[] into ordered footage entries. Never throws.
//
// Async because the video key hashes a sample of each file's BYTES (see
// videoKeys.js) — metadata alone cannot tell two Brinno chunks apart.
export async function scanVideos(files) {
  const entries = [];
  const skippedNonVideos = [];
  const seenKeys = new Set();
  let totalBytes = 0;
  let missingCaptureTime = 0;
  let duplicateCount = 0;

  for (const file of Array.from(files || [])) {
    if (!isVideoFile(file)) {
      skippedNonVideos.push(file);
      continue;
    }

    const capturedAt = Number.isFinite(file.lastModified) ? file.lastModified : null;
    if (capturedAt == null) missingCaptureTime += 1;

    // eslint-disable-next-line no-await-in-loop -- reads are sequential on
    // purpose: this opens up to 100 multi-GB files off an SD card, and firing
    // every slice at once buys nothing on one card reader while multiplying
    // the handles held open.
    const videoKey = await buildVideoKey(file);
    // Dedup within the drop itself — a user can pick overlapping folders. The
    // loser is COUNTED, not silently dropped: the key now covers content, so a
    // hit here means the same clip really was picked twice, and the dialog says
    // so rather than quietly shipping a shorter batch than the user chose.
    if (seenKeys.has(videoKey)) {
      duplicateCount += 1;
      continue;
    }
    seenKeys.add(videoKey);

    const sizeBytes = Number.isFinite(file.size) ? file.size : 0;
    entries.push({
      file,
      videoKey,
      fileName: file.name,
      capturedAt,
      capturedAtSource: capturedAt == null ? 'none' : 'fileModified',
      sizeBytes,
      // Filled in by the server probe; surfaced as "—" until then.
      durationSec: null,
    });
    totalBytes += sizeBytes;
  }

  const ordered = withOrder(defaultVideoOrder(entries));

  return {
    entries: ordered,
    skippedNonVideos,
    missingCaptureTime,
    // Files dropped as duplicates of another file in this same batch.
    duplicateCount,
    orderingBasis: orderingBasis(entries),
    totals: {
      videoCount: ordered.length,
      totalBytes,
      largestBytes: ordered.reduce((a, e) => Math.max(a, e.sizeBytes || 0), 0),
    },
  };
}
